import { Extension, ExtensionStyle } from "@/types/dto/pdp/pdp-itinerary";
import { ColletteCarousel } from "@collette-travel/collette-common-controls";
import { Box, Grid, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import React from "react";
import { ActivityLevel } from "@/types/dto/pdp/pdp-header-about";
import { useTranslations } from "next-intl";
import EnhanceYourTourCard from "./enhance-your-tour-card";
import EnhanceYourTourSkeleton from "./enhance-your-tour-skeleton";

interface EnhanceYourTourProps {
  preTour?: Extension;
  postTour?: Extension;
  isLoading: boolean;
  handleOpenExtensionStyles: (extensionStyle: string) => void;
  handleOpenActivityLevel: (defaultLevel: ActivityLevel["number"]) => void;
  extensionStyleProps: ExtensionStyle[];
}

const EnhanceYourTour = ({
  preTour,
  postTour,
  isLoading,
  handleOpenActivityLevel,
  handleOpenExtensionStyles,
  extensionStyleProps
}: EnhanceYourTourProps) => {
  const t = useTranslations("PDP");
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));

  const extensions = [
    { extension: preTour, type: "pre" as const },
    { extension: postTour, type: "post" as const }
  ].filter((item) => item.extension && item.extension.price != null);

  if (isLoading) {
    return <EnhanceYourTourSkeleton />;
  }

  if (extensions.length === 0) return null;

  const arrowPosition = () => {
    if (extensions.length < 2) {
      return "none";
    }
    if (isMd) {
      return "top-right";
    }
    return "bottom";
  };

  const renderCards = () =>
    extensions.map(({ extension, type }) => (
      <EnhanceYourTourCard
        key={`${type}-tour-extension`}
        extension={extension}
        handleOpenExtensionStyles={handleOpenExtensionStyles}
        handleOpenActivityLevel={handleOpenActivityLevel}
        extensionStyleProps={extensionStyleProps}
        type={type}
      />
    ));

  return (
    <Box
      className="fluid-box"
      component="section"
      data-track="visibility"
      data-parent-container-type="Card Carousel"
      data-module-name="enhance-your-tour-section"
      data-module-ranking-position="12"
      id="enhance-your-tour"
    >
      <Box
        className="destination-page-container"
        sx={{
          padding: {
            xs: "4rem 2.4rem",
            sm: "4rem 2.4rem",
            md: "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
          },
          display: "flex",
          flexDirection: "column",
          gap: { xs: "2.4rem", sm: "4rem" }
        }}
      >
        <Typography
          variant="h2"
          data-testid="enhance-your-tour-header"
          color="primary.main"
          sx={{ textAlign: extensions.length < 2 ? "center" : "start" }}
        >
          {t("Enhance Your Tour")}
        </Typography>
        {isLg ? (
          <Grid container spacing={2.5} justifyContent="center">
            {renderCards()}
          </Grid>
        ) : (
          <ColletteCarousel
            testId="enhance-your-tour-carousel"
            variant="multi-card"
            arrowPosition={arrowPosition()}
            slidesToScroll="auto"
            arrowContainerSx={{ top: isMd ? "-8.1rem" : "" }}
          >
            {renderCards()}
          </ColletteCarousel>
        )}
      </Box>
    </Box>
  );
};
export default EnhanceYourTour;
